import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABEL, formatEmployeeName, api } from "../lib/api";
import { Button } from "./ui/button";
import { LogOut, Home, FilePlus2, ListChecks, Stamp, Users, Settings, HomeIcon, ShieldAlert, BarChart3, CalendarRange, Clock } from "lucide-react";
import NotificationBell from "./NotificationBell";
import { ErrorBoundary } from "./ErrorBoundary";

const NavItem = ({ to, icon: Icon, children, testid }) => {
  const { pathname } = useLocation();
  const active = pathname === to || (to !== "/" && pathname.startsWith(to));
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
        active ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-foreground/80"
      }`}
    >
      <Icon className="w-4 h-4" /> {children}
    </Link>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const canApprove = ["manager","secretary","dean","admin","prodekan"].includes(user?.role) || !!user?.can_submit_for_team;
  const canDecide = ["secretary","dean","admin","prodekan"].includes(user?.role);
  const canManageEmployees = ["secretary","admin","dean","prodekan"].includes(user?.role);
  const canSeeAdmin = ["secretary","admin","dean","prodekan"].includes(user?.role);

  // Iter72 + Iter103: attendance feature toggle — admin uvijek vidi;
  // per-user override omogućava individualnim zaposlenicima da vide Prisutnost
  // čak i kad je globalna postavka isključena.
  const [attendanceVisible, setAttendanceVisible] = useState(true);
  useEffect(() => {
    if (!user) return;
    if (user.role === "admin") { setAttendanceVisible(true); return; }
    if (user.attendance_override_enabled) { setAttendanceVisible(true); return; }  // Iter103
    api.get("/settings").then(r => {
      const enabled = r.data?.attendance_enabled_for_non_admin;
      setAttendanceVisible(enabled !== false); // default true
    }).catch(() => { setAttendanceVisible(true); });
  }, [user]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-border bg-white/70 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <img
              src="/assets/kif-logo.jpg"
              alt="KIF"
              className="h-12 w-auto object-contain"
              data-testid="kif-logo"
            />
            <div className="hidden sm:block border-l border-border/60 pl-3">
              <div className="serif text-lg leading-tight font-semibold">KIF portal zaposlenika</div>
              <div className="text-xs text-muted-foreground tracking-wide">Osobni portal · Kineziološki fakultet</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="text-right hidden sm:block leading-tight">
              <div className="text-sm font-medium" data-testid="user-name">{formatEmployeeName(user)}</div>
              <div className="text-[11px] text-muted-foreground">{user?.custom_role_name || ROLE_LABEL[user?.role] || user?.role}</div>
              {user?.radno_mjesto && user.radno_mjesto !== user.katedra && (
                <div className="text-[11px] text-muted-foreground italic">{user.radno_mjesto}</div>
              )}
            </div>
            {/* Mobile: only show name */}
            <div className="text-right sm:hidden leading-tight">
              <div className="text-xs font-medium truncate max-w-[120px]">{user?.full_name?.split(" ")[0]}</div>
              <div className="text-[10px] text-muted-foreground">{user?.custom_role_name || ROLE_LABEL[user?.role] || user?.role}</div>
            </div>
            <Button variant="outline" size="sm" onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Odjava
            </Button>
          </div>
        </div>
        <div className="kif-line" />
        <nav className="max-w-7xl mx-auto px-6 py-2 flex flex-wrap gap-1">
          <NavItem to="/" icon={Home} testid="nav-home">Početna</NavItem>
          <NavItem to="/zahtjev" icon={FilePlus2} testid="nav-new">Novi godišnji odmor</NavItem>
          <NavItem to="/wfh/novi" icon={HomeIcon} testid="nav-new-wfh">Novi rad na daljinu</NavItem>
          <NavItem to="/zahtjevi" icon={ListChecks} testid="nav-mine">Moji zahtjevi</NavItem>
          {attendanceVisible && <NavItem to="/prisutnost" icon={Clock} testid="nav-attendance">Prisutnost</NavItem>}
          {canApprove && <NavItem to="/odobrenja" icon={Users} testid="nav-approvals">Suglasnosti</NavItem>}
          {canApprove && <NavItem to="/kalendar-tima" icon={CalendarRange} testid="nav-team-calendar">Kalendar tima</NavItem>}
          {canDecide && <NavItem to="/dekanat" icon={Stamp} testid="nav-secretariat">Tajništvo</NavItem>}
          {canApprove && <NavItem to="/postavke" icon={Settings} testid="nav-settings">Postavke</NavItem>}
          {canManageEmployees && <NavItem to="/zaposlenici" icon={Users} testid="nav-employees">Zaposlenici</NavItem>}
          {canSeeAdmin && <NavItem to="/statistika" icon={BarChart3} testid="nav-stats">Statistika</NavItem>}
          {canSeeAdmin && <NavItem to="/administracija" icon={ShieldAlert} testid="nav-admin">Administracija</NavItem>}
        </nav>
      </header>
      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-8">
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>
      <footer className="border-t border-border py-4 text-center text-xs text-muted-foreground">
        © Kineziološki fakultet, Sveučilište u Zagrebu · Horvaćanski zavoj 15, 10000 Zagreb
      </footer>
    </div>
  );
}
